import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { Order, VisitSortingFields, VisitsFiltersProps, VisitsListModel } from '../models';

interface IVisitsListContext {
  visitsUpdate: boolean;
  toggleVisitsUpdate: () => void;
  visits: VisitsListModel[];
  setVisits: (val: VisitsListModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VisitSortingFields;
  setOrderBy: (val: VisitSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: VisitSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  setFilters: (val: VisitsFiltersProps) => void;
  filters: VisitsFiltersProps;
  resetFilters: () => void;
}

export const VisitsListContext = createContext<IVisitsListContext>({} as IVisitsListContext);

export const VisitsListProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [visitsUpdate, setVisitsUpdate] = useState(false);
  const toggleUpdate = (): void => setVisitsUpdate(!visitsUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<VisitSortingFields>(toggleUpdate, 'startedAt', 'desc');

  const [visits, setVisits] = useState<VisitsListModel[]>([]);
  const [loading, setLoading] = useState(false);
  const emptyFilters: VisitsFiltersProps = {
    venue: undefined,
    status: undefined,
    serviceName: undefined,
    from: undefined,
    to: undefined,
    mainTechnician: undefined,
    client: undefined
  };
  const [filters, setFilters] = useState<VisitsFiltersProps>(emptyFilters);
  const resetFilters = (): void => setFilters({});

  return (
    <VisitsListContext.Provider
      value={{
        visitsUpdate,
        resetFilters,
        toggleVisitsUpdate: toggleUpdate,
        visits,
        setVisits,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        filters,
        setFilters
      }}
    >
      {children}
    </VisitsListContext.Provider>
  );
};
